import { useEffect, useRef, useCallback, useState } from "react";
import { wsClient } from "@/lib/websocket";

interface UseWebSocketOptions {
  role: "customer" | "admin";
  userId: string;
  conversationId?: string;
}

const CHAT_CACHE_KEY = "mll_chat_cache";
const CHAT_CACHE_TTL = 24 * 60 * 60 * 1000; // 24 hours

function loadCachedMessages(convId: string): any[] {
  try {
    const raw = localStorage.getItem(CHAT_CACHE_KEY);
    if (!raw) return [];
    const cache = JSON.parse(raw);
    if (cache.conversationId !== convId) return [];
    if (Date.now() - cache.timestamp > CHAT_CACHE_TTL) {
      localStorage.removeItem(CHAT_CACHE_KEY);
      return [];
    }
    return cache.messages || [];
  } catch { return []; }
}

function saveCachedMessages(convId: string, messages: any[]) {
  try {
    // Keep last 100 messages to prevent localStorage bloat
    const trimmed = messages.slice(-100);
    localStorage.setItem(CHAT_CACHE_KEY, JSON.stringify({
      conversationId: convId,
      messages: trimmed,
      timestamp: Date.now(),
    }));
  } catch {}
}

export function useWebSocket({ role, userId, conversationId }: UseWebSocketOptions) {
  const [isConnected, setIsConnected] = useState(false);
  // Load cached messages for customers (chat history persistence)
  const [messages, setMessages] = useState<any[]>(() =>
    role === "customer" && conversationId ? loadCachedMessages(conversationId) : []
  );
  const [isTyping, setIsTyping] = useState(false);
  const [agentAvailable, setAgentAvailable] = useState(false);
  const typingTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    wsClient.connect(role, userId);

    const unsubs = [
      wsClient.on("connected", () => setIsConnected(true)),
      wsClient.on("disconnected", () => setIsConnected(false)),
      wsClient.on("new_message", (data) => {
        if (!data.self) {
          setMessages((prev) => {
            // Deduplicate by id
            if (prev.some((m) => m.id === data.message.id)) return prev;
            return [...prev, data.message];
          });
        }
      }),
      wsClient.on("user_typing", () => setIsTyping(true)),
      wsClient.on("user_stop_typing", () => setIsTyping(false)),
      wsClient.on("agent_availability", (data) => setAgentAvailable(data.available)),
    ];

    return () => { unsubs.forEach((u) => u()); };
  }, [role, userId]);

  // When conversationId changes, join room AND fetch history
  useEffect(() => {
    if (!conversationId) return;

    wsClient.send({ type: "join_conversation", conversationId });

    // Fetch existing messages from REST API
    fetch(`/api/conversations/${conversationId}/messages?limit=50`, {
      credentials: "include",
    })
      .then((res) => {
        if (res.ok) return res.json();
        return [];
      })
      .then((history: any[]) => {
        if (history.length > 0) {
          // History comes newest-first from API, reverse to chronological
          const sorted = [...history].reverse();
          // History is authoritative — replace all messages, dropping local optimistic ones
          setMessages(sorted);
        }
      })
      .catch(() => { /* silently fail — WS messages still work */ });
  }, [conversationId]);

  // Cache messages for customer chat persistence (24hr)
  useEffect(() => {
    if (role === "customer" && conversationId && messages.length > 0) {
      saveCachedMessages(conversationId, messages);
    }
  }, [role, conversationId, messages]);

  const sendMessage = useCallback((content: string, opts?: {
    messageType?: string;
    attachmentUrl?: string;
    attachmentDuration?: number;
  }) => {
    const msg: Record<string, any> = {
      type: "send_message",
      conversationId: conversationId || "new",
      content,
      messageType: opts?.messageType || "text",
      attachmentUrl: opts?.attachmentUrl,
      attachmentDuration: opts?.attachmentDuration,
      visitorName: typeof window !== 'undefined' ? localStorage.getItem("mll_visitor_name") : undefined,
      visitorPhone: typeof window !== 'undefined' ? localStorage.getItem("mll_visitor_phone") : undefined,
    };
    wsClient.send(msg);
    setMessages((prev) => [...prev, {
      id: `local_${Date.now()}`,
      conversationId,
      senderRole: role === "admin" ? "agent" : "customer",
      senderId: userId,
      messageType: opts?.messageType || "text",
      content,
      attachmentUrl: opts?.attachmentUrl,
      attachmentDuration: opts?.attachmentDuration,
      status: "sent",
      createdAt: new Date().toISOString(),
    }]);
  }, [conversationId, role, userId]);

  const sendTyping = useCallback(() => {
    wsClient.send({ type: "typing" });
    if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    typingTimeoutRef.current = setTimeout(() => {
      wsClient.send({ type: "stop_typing" });
    }, 3000);
  }, []);

  const markRead = useCallback(() => {
    wsClient.send({ type: "mark_read" });
  }, []);

  return {
    isConnected,
    messages,
    setMessages,
    isTyping,
    agentAvailable,
    sendMessage,
    sendTyping,
    markRead,
  };
}
